import type { AudioSettingsStore } from "../../audio/AudioSettingsStore";
import type { AudioSettingsService } from "../../audio/AudioSettingsService";
import type { HapticsSettingsStore } from "../../haptics/HapticsSettingsStore";
import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { SpinCoordinator } from "../../spin/SpinCoordinator";
import type { ShellMenuPort, ShellMenuTogglePort } from "./ShellMenuPorts";
import type { ViewportStore } from "@slot/presentation";

/** The info page and game history presenters, as the menu drives them. */
export interface ShellMenuPageOpener {
  openPage(): void;
  isOpen(): boolean;
  close(): void;
}

export interface ShellMenuPresenterDeps {
  audioSettingsStore: AudioSettingsStore;
  audioSettingsService: AudioSettingsService;
  hapticsSettingsStore: HapticsSettingsStore;
  interactionBlockStore: InteractionBlockStore;
  /** Operator setting: the History row is offered. */
  gameHistoryEnabled: boolean;
  viewport: ViewportStore;
  /** The Home row: the operator's lobby, when the launch context names one. */
  onHome: () => void;
}

/**
 * The settings menu: the bar toggle opens it (a popover on desktop, a blocking panel on mobile);
 * sound, volume, music and vibration write the settings stores; History (while a round is not in
 * flight) and Info open their pages and close the menu.
 */
export class ShellMenuPresenter {
  private open = false;
  private unsubscribe: Array<() => void> = [];

  public constructor(
    private readonly panel: ShellMenuPort,
    private readonly toggle: ShellMenuTogglePort,
    private readonly deps: ShellMenuPresenterDeps,
    /** Null when the game ships no info page — the Info row stays disabled. */
    private readonly infoPagePresenter: ShellMenuPageOpener | null,
    private readonly spinCoordinator: SpinCoordinator,
    private readonly onBeforeOpen?: () => void,
    private readonly gameHistoryPresenter: ShellMenuPageOpener | null = null,
  ) {
    this.panel.bind({
      onBackdrop: () => this.close(),
      onClose: () => this.close(),
      onSound: () => this.deps.audioSettingsService.toggleMute(),
      onMasterVolumeChange: (volume) => this.deps.audioSettingsService.setMasterVolume(volume),
      onMusic: () => this.deps.audioSettingsService.toggleMusic(),
      onVibration: () => this.deps.hapticsSettingsStore.toggle(),
      onInfo: () => this.openInfo(),
      onHome: () => this.deps.onHome(),
      onHistory: () => this.openHistory(),
      onOutsideClick: () => this.close(),
    });
    this.toggle.setOnTap(() => this.toggleMenu());
    this.toggle.setOpen(false);
  }

  public start(): void {
    this.open = false;
    this.panel.setMenuOpen(false);
    this.toggle.setOpen(false);
    this.unsubscribe = [
      this.deps.audioSettingsStore.subscribe(() => this.render()),
      this.deps.hapticsSettingsStore.subscribe(() => this.render()),
      // A shape change re-applies the blocking rule while open.
      this.deps.viewport.subscribe(() => {
        if (this.open) this.applyInteractionBlock();
        this.render();
      }),
      // The History row follows `canSpin`: disabled while a round is in flight.
      this.spinCoordinator.subscribe(() => this.render()),
    ];
    this.render();
  }

  public stop(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe = [];
    this.close();
  }

  public isOpen(): boolean {
    return this.open;
  }

  public render(): void {
    const audio = this.deps.audioSettingsStore.getSnapshot();
    // "Sound on" is a non-zero master volume; the icon button mutes and restores the saved volume.
    const soundOn = audio.masterVolume > 0;
    this.panel.setSoundEnabled(soundOn);
    this.panel.setMasterVolume(audio.masterVolume);
    this.panel.setMusicEnabled(soundOn && audio.musicEnabled);
    this.panel.setMusicInteractable(soundOn);
    this.panel.setVibrationEnabled(this.deps.hapticsSettingsStore.getSnapshot().hapticsEnabled);
    const historyAvailable = this.deps.gameHistoryEnabled && this.gameHistoryPresenter !== null;
    this.panel.setHistoryButtonVisible(historyAvailable);
    this.panel.setHistoryButtonInteractable(historyAvailable && this.spinCoordinator.getSnapshot().canSpin);
    this.panel.setInfoButtonInteractable(this.infoPagePresenter !== null);
  }

  public close(): void {
    this.open = false;
    this.toggle.setOpen(false);
    this.panel.setMenuOpen(false);
    this.deps.interactionBlockStore.remove("menu");
  }

  private toggleMenu(): void {
    if (this.open) this.close();
    else this.openMenu();
  }

  private openMenu(): void {
    this.onBeforeOpen?.();
    this.open = true;
    this.toggle.setOpen(true);
    this.panel.setMenuOpen(true);
    this.applyInteractionBlock();
    this.render();
  }

  /** The mobile panel blocks the game; the desktop popover leaves it live (a press outside closes it). */
  private applyInteractionBlock(): void {
    if (this.deps.viewport.getSnapshot().shape === "desktop") this.deps.interactionBlockStore.remove("menu");
    else this.deps.interactionBlockStore.add("menu");
  }

  private openInfo(): void {
    if (!this.infoPagePresenter) return;
    this.close();
    this.infoPagePresenter.openPage();
  }

  private openHistory(): void {
    if (!this.gameHistoryPresenter || !this.spinCoordinator.getSnapshot().canSpin) return;
    this.close();
    this.gameHistoryPresenter.openPage();
  }
}
